import { Spinner } from '@arpeggi/ui'
import { observer } from 'mobx-react-lite'
import { ComponentProps, FC, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ArpRegistry from '../../../../../features/arp-registry/ArpRegistry'
import { CSS, styled } from '../../../../lib/stitches'
import ProfileRegistry, { IProfile } from '../../../../stores/registry/ProfileRegistry'
import { ArpMediaType } from '../../../../types/common'
import { capitalizeFirstLetter } from '../../../../utils/strings'
import { Box, Column, Row } from '../../../core/Components'
import { Flex } from '../../../core/Flex'
import { Modal } from '../../../core/Modal'
import Text from '../../../core/Text/Text'
import withTooltip from '../../../core/withTooltip'
import Badge from '../../../player/common/Badge'
import ProfileAvatar from './ProfileAvatar'
import { Collaborator, useCollaborators } from './utils'

const Container = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '24px'
})

const AvatarsContainer = withTooltip(
  styled('div', {
    display: 'flex',
    flexDirection: 'row-reverse',
    transform: 'translateX(7px)'
  })
)

const modalSidePadding: Partial<CSS> = {
  paddingLeft: '$24',
  paddingRight: '$24'
}

const StyledContributorModalColumn = styled(Column, {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minWidth: '180px'
})

const StyledContributersHeader = styled(Row, {
  ...modalSidePadding,
  borderColor: '$gray04',
  borderTopWidth: '1px',
  borderBottomWidth: '1px',
  borderTopStyle: 'solid',
  borderBottomStyle: 'solid',
  paddingTop: '$10',
  paddingBottom: '$10',
  width: 'inherit'
})

const useContributerModalRow = (collab: Collaborator) => {
  const [isLoading, setIsLoading] = useState(true)
  const [artist, setArtist] = useState<IProfile>(null)
  const [mediaTitle, setMediaTitle] = useState<string>(null)

  const colorMap = {
    [ArpMediaType.SONG]: '#F3D4EB',
    [ArpMediaType.SAMPLE]: '#D4ECF3',
    [ArpMediaType.STEM]: '#E4F8D1'
  }

  useEffect(() => {
    const setup = async () => {
      setIsLoading(true)

      const artist = await ProfileRegistry.getByIndexAsync(collab.ethAddress)
      setArtist(artist)

      const media = await ArpRegistry.getByIdAsync(collab.mediaIndex)
      setMediaTitle(media.metadata.title)

      setIsLoading(false)
    }

    setup().catch((error) => console.error('Setup error for contributor', error))
  }, [collab])

  return {
    isLoading,
    artist,
    mediaTitle,
    mediaTypeTitle:
      collab.mediaType === ArpMediaType.SONG ? 'Composer' : capitalizeFirstLetter(collab.mediaType),
    color: colorMap[collab.mediaType]
  }
}

interface ContriubutorModalRowProps {
  collab: Collaborator
}

const ContributorModalRow: React.FC<ContriubutorModalRowProps> = ({ collab }) => {
  const { isLoading, artist, mediaTitle, mediaTypeTitle, color } = useContributerModalRow(collab)

  if (isLoading) {
    return null
  }

  return (
    <Row css={{ ...modalSidePadding, paddingTop: '$10', paddingBottom: '$10' }}>
      <StyledContributorModalColumn>
        <Link to={`/profile/${artist.wallet_address}`} target="_blank">
          <Flex
            css={{ alignItems: 'center', gap: '16px', '&:hover': { textDecoration: 'underline' } }}
          >
            <ProfileAvatar eth={artist.wallet_address} />
            <Text size="12">{artist.display_name}</Text>
          </Flex>
        </Link>
      </StyledContributorModalColumn>
      <StyledContributorModalColumn>
        <Badge
          css={{
            fontFamily: '$poppins',
            width: 'fit-content',
            animation: 'none',
            backgroundColor: color
          }}
        >
          {mediaTypeTitle}
        </Badge>
      </StyledContributorModalColumn>
      <StyledContributorModalColumn>
        <Link to={`/${collab.mediaType}?token=${collab.mediaIndex}`} target="_blank">
          <Text
            size="12"
            css={{ whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}
          >
            {mediaTitle}
          </Text>
        </Link>
      </StyledContributorModalColumn>
    </Row>
  )
}

type ContributorsModalProps = {
  collabs: Collaborator[]
  mediaIndex: number
  mediaType: ArpMediaType
} & ComponentProps<typeof Modal>

const ContributorsModal: React.FC<ContributorsModalProps> = ({
  collabs,
  mediaIndex,
  mediaType,
  ...props
}) => (
  <Modal
    {...props}
    contentCss={{ padding: 0 }}
    closeButtonCss={{ top: '15px', right: '15px', overflow: 'hidden' }}
  >
    <Text
      size="16"
      type="primary"
      css={{ ...modalSidePadding, paddingTop: '$16', paddingBottom: '$16' }}
    >
      Contributors
    </Text>
    <Box css={{ width: '600px', maxWidth: '100%', overflowX: 'auto', position: 'relative' }}>
      <StyledContributersHeader>
        <StyledContributorModalColumn>
          <Text size="14" type="primary" css={{ fontWeight: '$medium' }}>
            Artist
          </Text>
        </StyledContributorModalColumn>
        <StyledContributorModalColumn>
          <Text size="14" type="primary">
            Type
          </Text>
        </StyledContributorModalColumn>
        <StyledContributorModalColumn>
          <Text size="14" type="primary">
            Title
          </Text>
        </StyledContributorModalColumn>
      </StyledContributersHeader>
      <Box css={{ paddingBottom: '$24' }}>
        {collabs.map((collab) => (
          <ContributorModalRow collab={collab} key={`${collab.mediaType}-${collab.mediaIndex}`} />
        ))}
      </Box>
    </Box>
  </Modal>
)

type ContributorsDisplayProps = {
  collabs: Collaborator[]
  status: 'loading' | 'ready' | 'error'
  mediaType: ArpMediaType
  mediaIndex: number
  uniqueCollabEthAddresses: string[]
} & Omit<ContributorsProps, 'index' | 'type'>

export const ContributorsDisplay: FC<ContributorsDisplayProps> = ({
  collabs,
  avatarSize,
  noText,
  status,
  maxContributors = Infinity,
  mediaIndex,
  mediaType,
  uniqueCollabEthAddresses,
  ...props
}) => {
  const navigate = useNavigate()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const maxContributorsToShow = Math.min(maxContributors, uniqueCollabEthAddresses.length)

  const onModalOpenChange = (isOpen: boolean) => setIsModalOpen(isOpen)

  return (
    <>
      <ContributorsModal
        open={isModalOpen}
        onOpenChange={onModalOpenChange}
        collabs={collabs}
        mediaIndex={mediaIndex}
        mediaType={mediaType}
      />
      <Container {...props}>
        {!noText && (
          <Text size={'14'} css={{ fontWeight: '$medium' }}>
            Contributors
          </Text>
        )}
        <AvatarsContainer tooltipText="View more" onClick={() => setIsModalOpen(true)}>
          {status === 'loading' && <Spinner />}
          {maxContributorsToShow !== uniqueCollabEthAddresses.length && (
            <Box as="span" css={{ display: 'flex', alignItems: 'end' }}>
              ...
            </Box>
          )}
          {status === 'ready' &&
            [...uniqueCollabEthAddresses.slice(0, maxContributorsToShow)].map((ethAddress) => (
              <ProfileAvatar
                css={{
                  width: avatarSize,
                  height: avatarSize,
                  marginLeft: '-7px',
                  cursor: 'pointer'
                }}
                bordered
                eth={ethAddress}
                key={ethAddress}
                showLink={false}
              />
            ))}
          {status === 'error' && <Text>Error fetching contributors</Text>}
        </AvatarsContainer>
      </Container>
    </>
  )
}

type ContributorsProps = {
  index: number
  type: ArpMediaType
  avatarSize?: string
  noText?: boolean
  maxContributors?: number
} & ComponentProps<typeof Container>

const Contributors: FC<ContributorsProps> = ({ index, type, ...props }) => {
  const { collabs, status, uniqueCollabEthAddresses } = useCollaborators(type, index.toString())

  return (
    <ContributorsDisplay
      collabs={collabs}
      uniqueCollabEthAddresses={uniqueCollabEthAddresses}
      status={status}
      mediaType={type}
      mediaIndex={index}
      {...props}
    />
  )
}

export default observer(Contributors)
